import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import gql from "graphql-tag";
import { useQuery } from "@apollo/react-hooks";
import { ApolloClient, ApolloError } from "@apollo/client";

// The live params shown on a live card, in the order they are asked for.
export const LIVE_PARAM_NAMES = [
  "pv.power",
  "inverter.ac_out_power",
  "inverter.heat_sink_temp",
  "battery.soc",
  "battery.total_voltage",
  "liquids.level0",
  "liquids.level1",
  "liquids.level2",
  "liquids.level5",
  "liquids.level3",
  "liquids.level4",
];

export interface LiveParamNamed {
  name: string;
  value: number | null;
  updatedAt: string | null;
}

export interface Alert {
  name: string;
  message: string;
  level: string;
}

// The live data of one order. The params or alerts are null when they could
// not be read from the device.
export interface LiveData {
  params: LiveParamNamed[] | null;
  alerts: Alert[] | null;
}

interface LiveDatasQueryResult {
  dcxLiveDatas?: { data: ({ orderID: string } & LiveData)[] };
}

// The live params and alerts of every order of a grid of live cards, in one
// call.
export const DCX_LIVE_DATAS_QUERY = gql`
  query dcxLiveDatas($orderIDs: [ID!]!, $names: [String!]!) {
    dcxLiveDatas(orderIDs: $orderIDs, input: { names: $names }) {
      data {
        orderID
        params {
          name
          value
          updatedAt
        }
        alerts {
          name
          message
          level
        }
      }
    }
  }
`;

export interface LiveDatas {
  // the live data of each order that has any
  byOrderID: Map<string, LiveData>;
  // whether a response has arrived yet
  read: boolean;
  // when the last response arrived, in milliseconds since the epoch
  readAt: number;
  error?: ApolloError;
}

// The live data of the given orders, read in one call and again every
// pollInterval milliseconds. The IDs must be memoised, or the query is sent
// again on every render.
export function useLiveDatas(
  orderIDs: string[],
  client: ApolloClient<unknown>,
  pollInterval = 60000
): LiveDatas {
  const { data, error, refetch } = useQuery<LiveDatasQueryResult>(
    DCX_LIVE_DATAS_QUERY,
    {
      client,
      fetchPolicy: "no-cache",
      // the orders that could be read are shown when another could not be
      errorPolicy: "all",
      variables: { orderIDs, names: LIVE_PARAM_NAMES },
      skip: 0 === orderIDs.length,
    }
  );

  // Apollo's built-in `pollInterval` has a bug where, from the second poll
  // tick onward, the re-render triggered by a poll's response can itself
  // trigger an immediate extra refetch, which cascades into a self-sustaining
  // fetch storm. Driving the interval ourselves sidesteps it.
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  });
  useEffect(() => {
    if (!pollInterval || 0 === orderIDs.length) return;
    const id = setInterval(() => {
      refetchRef.current();
    }, pollInterval);
    return (): void => clearInterval(id);
  }, [orderIDs, pollInterval]);

  const byOrderID = useMemo(() => {
    const m = new Map<string, LiveData>();
    data?.dcxLiveDatas?.data.forEach(({ orderID, params, alerts }) => {
      m.set(orderID, { params, alerts });
    });
    return m;
  }, [data]);

  // when the last response arrived, so that an order's online status moves on
  // with each poll
  const [readAt, setReadAt] = useState(() => Date.now());
  useEffect(() => {
    setReadAt(Date.now());
  }, [data, error]);

  const read = undefined !== data || undefined !== error;
  return { byOrderID, read, readAt, error };
}

// A device is offline when it has not reported for this long.
export const OFFLINE_AFTER_MS = 5 * 60 * 1000;

// When the order's device last reported, as shown on its live card as Updated.
export function lastUpdatedAt(data?: LiveData): string | undefined {
  return (
    data?.params?.find((p) => p.name === "pv.power")?.updatedAt || undefined
  );
}

// unknown until the live data has been read
export type OnlineStatus = "online" | "offline" | "unknown";

// Whether an order's device is online at the time now: it is offline when it
// could not be read, has never reported, or last reported more than
// OFFLINE_AFTER_MS ago.
export function onlineStatus(
  data: LiveData | undefined,
  read: boolean,
  now: number
): OnlineStatus {
  if (!read) return "unknown";
  const updatedAt = lastUpdatedAt(data);
  if (!updatedAt) return "offline";
  const t = Date.parse(updatedAt);
  if (isNaN(t) || now - t > OFFLINE_AFTER_MS) return "offline";
  return "online";
}

export interface OnlineStatuses {
  statusOf: (orderID: string) => OnlineStatus;
  online: number;
  offline: number;
}

// The online status of each of the given orders, and how many are online and
// offline.
export function useOnlineStatuses(
  orderIDs: string[],
  { byOrderID, read, readAt }: LiveDatas
): OnlineStatuses {
  const statusOf = useCallback(
    (orderID: string): OnlineStatus =>
      onlineStatus(byOrderID.get(orderID), read, readAt),
    [byOrderID, read, readAt]
  );
  return useMemo(() => {
    let online = 0;
    let offline = 0;
    orderIDs.forEach((id) => {
      const s = statusOf(id);
      if ("online" === s) online++;
      else if ("offline" === s) offline++;
    });
    return { statusOf, online, offline };
  }, [orderIDs, statusOf]);
}
